import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Operation } from '@data/models/inventory.model';
import { Project } from '@data/models/project.model';
import { ProjectService } from '@data/services/project.service';
import { UserService } from '@data/services/user.service';
import { map, Subscription } from 'rxjs';
import { BusinessProjectService } from '@data/services/business-project.service';
import { AutocompleteDataLoader, AutocompletePagination, Displayable, EntityNameResolver } from '../autocomplete/autocomplete.component';
import { BulkAction, BulkEditToggleOption } from '../bulk-edit-toggle/bulk-edit-toggle.component';

interface Metadata {
    key: string;
    value: string;
}

@Component({
    selector: 'syn-project-bulk-edit',
    templateUrl: './project-bulk-edit.component.html',
    styleUrls: ['./project-bulk-edit.component.scss']
})
export class ProjectBulkEditComponent implements OnInit, OnDestroy {
    @Input() projects: Project[] = [];
    @Input() updateUsers = false;

    @Output() close = new EventEmitter();
    @Output() updated = new EventEmitter();
    @ViewChild('stepper') stepper: MatStepper;

    readonly businessProjectInvalidIdRegex = new RegExp('^BPJ([A-Z0-9]{0,5})$');

    readonly appManagerOptions: BulkEditToggleOption[] = [
        { label: 'Ajouter', value: BulkAction.ADD },
        { label: 'Supprimer', value: BulkAction.REMOVE }
    ];

    readonly modifyOnlyOptions: BulkEditToggleOption[] = [
        { label: 'Modifier', value: BulkAction.MODIFY }
    ];

    loading = false;
    bulkEditForm: FormGroup;
    tagForm: FormGroup;
    addMetadataForm: FormGroup;
    removeMetadataForm: FormGroup;

    tagsToAdd: string[] = [];
    tagsToRemove: string[] = [];
    metadatasToAdd: Metadata[] = [];
    metadatasToRemove: string[] = [];

    updatedProjects: Project[] = [];
    untouchedProjects: Project[] = [];
    errors: { project: Project; error: string }[] = [];

    projectUpdatedChartData: { name: string; value: number }[] | undefined = undefined;
    projectUpdatedChartScheme = {
        domain: [
            getComputedStyle(document.documentElement).getPropertyValue('--clara-alternative--background'),
            getComputedStyle(document.documentElement).getPropertyValue('--clara-alternative'),
            getComputedStyle(document.documentElement).getPropertyValue('--clara-red')
        ]
    };

    entityNameResolver = new EntityNameResolver();

    private bulkEditProjectSubscription?: Subscription;

    constructor(
        private readonly fb: FormBuilder,
        private readonly projectService: ProjectService,
        private readonly userService: UserService,
        private readonly businessProjectService: BusinessProjectService
    ) { }

    get hasUpdateParameters(): boolean {
        const { tagsAction, metadataAction, buildProjectManagerAction, deliveryManagerAction,
            backupDeliveryManagerAction, buildProjectManager, deliveryManager, backupDeliveryManager,
            businessProjectAction, businessProject, appManagerAction, addAppManager, removeAppManager } = this.bulkEditForm.value;

        const updatable = (action: BulkAction, field?: string) =>
            action === BulkAction.MODIFY && Boolean(field);

        const updatableOrRemovable = (action: BulkAction, field?: string) =>
            updatable(action, field) || action === BulkAction.REMOVE;

        return updatableOrRemovable(buildProjectManagerAction, buildProjectManager) ||
            updatableOrRemovable(deliveryManagerAction, deliveryManager) ||
            updatableOrRemovable(backupDeliveryManagerAction, backupDeliveryManager) ||
            updatableOrRemovable(businessProjectAction, businessProject) ||
            tagsAction === BulkAction.MODIFY && (this.tagsToAdd.length > 0 || this.tagsToRemove.length > 0) ||
            metadataAction === BulkAction.MODIFY && (this.metadatasToAdd.length > 0 || this.metadatasToRemove.length > 0) ||
            appManagerAction === 'add' && Boolean(addAppManager) || appManagerAction === 'remove' && Boolean(removeAppManager);
    }

    /* Actions */

    isChanged(actionControlName: string): boolean {
        return this.bulkEditForm.get(actionControlName).value !== BulkAction.UNCHANGED;
    }

    isModified(actionControlName: string): boolean {
        return this.bulkEditForm.get(actionControlName).value === BulkAction.MODIFY;
    }

    isRemoved(actionControlName: string): boolean {
        return this.bulkEditForm.get(actionControlName).value === BulkAction.REMOVE;
    }

    isAdded(actionControlName: string): boolean {
        return this.bulkEditForm.get(actionControlName).value === BulkAction.ADD;
    }

    /* Data loaders */

    loadUserData: AutocompleteDataLoader<Displayable> = (filter: string, { page, itemPerPage }: AutocompletePagination) => {
        return this.userService.searchUsers({ filter: filter.trim(), page, limit: itemPerPage })
            .pipe(map(response => {
                const displayables = response.users.map(user => ({ id: user.id, name: user.fullName }));
                this.entityNameResolver.listen(/USR-.+/.test(filter), displayables);

                return {
                    items: displayables,
                    totalSize: response.totalSize
                };
            }));
    };

    loadBusinessProjectData: AutocompleteDataLoader<Displayable> = (filter: string, { page, itemPerPage }: AutocompletePagination) => {
        return this.businessProjectService.findBusinessCodes({ filter: filter.trim(), page, limit: itemPerPage })
            .pipe(map(response => {
                const displayables = response.map(businessProject => ({ id: businessProject.businessCode, name: businessProject.businessLabel }));
                this.entityNameResolver.listen(/BPJ.+/.test(filter), displayables);

                return {
                    items: displayables,
                    totalSize: undefined
                };
            }));
    };

    ngOnInit(): void {
        this.bulkEditForm = this.fb.group({
            tagsAction: [BulkAction.UNCHANGED],
            metadataAction: [BulkAction.UNCHANGED],
            buildProjectManagerAction: [BulkAction.UNCHANGED],
            deliveryManagerAction: [BulkAction.UNCHANGED],
            backupDeliveryManagerAction: [BulkAction.UNCHANGED],
            businessProjectAction: [BulkAction.UNCHANGED],
            appManagerAction: [BulkAction.UNCHANGED],

            buildProjectManager: [],
            deliveryManager: [],
            backupDeliveryManager: [],
            addAppManager: [],
            removeAppManager: [],
            businessProject: []
        });

        this.tagForm = this.fb.group({
            addTag: [''],
            removeTag: ['']
        });

        this.addMetadataForm = this.fb.group({
            key: ['', Validators.required],
            value: ['', Validators.required]
        });

        this.removeMetadataForm = this.fb.group({
            key: ['', Validators.required]
        });
    }

    ngOnDestroy(): void {
        this.bulkEditProjectSubscription?.unsubscribe();
    }

    projectPluralisation(projectCount: number) {
        return `${projectCount} ${projectCount > 1 ? 'projets' : 'projet'}`;
    }

    addNewTag() {
        const control = this.tagForm.get('addTag');
        this.tagsToAdd = this.addTag(control, this.tagsToAdd);
    }

    addRemoveTag() {
        const control = this.tagForm.get('removeTag');
        this.tagsToRemove = this.addTag(control, this.tagsToRemove);
    }

    removeAddTag(tag: string) {
        this.tagsToAdd = this.tagsToAdd.filter(t => t !== tag);
    }

    removeRemoveTag(tag: string) {
        this.tagsToRemove = this.tagsToRemove.filter(t => t !== tag);
    }

    addMetadata() {
        if (!this.addMetadataForm.valid) {
            return;
        }

        const { key, value } = this.addMetadataForm.value;
        if (!key?.trim()) {
            return;
        }
        const existingIndex = this.metadatasToAdd.findIndex(m => m.key === key);

        if (existingIndex >= 0) {
            this.metadatasToAdd[existingIndex].value = value;
        } else {
            this.metadatasToAdd.push({ key, value });
        }

        this.addMetadataForm.reset();
    }

    removeMetadata() {
        if (!this.removeMetadataForm.valid) {
            return;
        }

        const { key } = this.removeMetadataForm.value;
        if (!key?.trim()) {
            return;
        }

        if (!this.metadatasToRemove.includes(key)) {
            this.metadatasToRemove.push(key);
        }

        this.removeMetadataForm.reset();
    }

    removeAddMetadata(key: string) {
        this.metadatasToAdd = this.metadatasToAdd.filter(m => m.key !== key);
    }

    removeRemoveMetadata(key: string) {
        this.metadatasToRemove = this.metadatasToRemove.filter(k => k !== key);
    }

    submit() {
        this.loading = true;
        this.stepper.next();

        this.bulkEditProjectSubscription = this.projectService.bulkEditProject(
            this.projects.map(project => project.id),
            this.getOperations()
        ).subscribe(({ updatedProjectIds, errors }) => {
            this.loading = false;
            this.updatedProjects = [];
            this.untouchedProjects = [];
            this.errors = [];

            for (const project of this.projects) {
                const updatedProject = updatedProjectIds.find(id => id === project.id);

                if (updatedProject) {
                    this.updatedProjects.push(project);
                    continue;
                }

                if (errors[project.id]) {
                    this.errors.push({ project, error: errors[project.id] });
                    continue;
                }

                this.untouchedProjects.push(project);
            }

            this.projectUpdatedChartData = [
                { name: 'Projets mis à jour', value: Math.round(this.updatedProjects.length * 100 / this.projects.length) },
                { name: 'Projets inchangés', value: Math.round(this.untouchedProjects.length * 100 / this.projects.length) },
                { name: 'Projets en erreur', value: Math.round(this.errors.length * 100 / this.projects.length) }
            ];

            this.updated.emit();
        });
    }

    closePanel() {
        this.close.emit();
    }

    private addTag(control: AbstractControl, tags: string[]): string[] {
        const tag = (control.value || '').trim();
        if (!tag?.length) {
            return tags;
        }

        const updatedTags = new Set(tags);
        updatedTags.add(tag);
        control.reset();
        return Array.from(updatedTags);
    }

    private getOperations(): Operation[] {
        const { tagsAction, metadataAction, buildProjectManagerAction,
            deliveryManagerAction, backupDeliveryManagerAction, businessProjectAction,
            appManagerAction } = this.bulkEditForm.value;
        const operations: Operation[] = [];

        if (tagsAction === BulkAction.MODIFY) {
            if (this.tagsToAdd.length > 0) {
                operations.push({
                    op: 'ADD',
                    path: 'tags',
                    value: this.tagsToAdd
                });
            }

            if (this.tagsToRemove.length > 0) {
                operations.push({
                    op: 'REMOVE',
                    path: 'tags',
                    value: this.tagsToRemove
                });
            }
        }

        if (metadataAction === BulkAction.MODIFY) {
            if (this.metadatasToAdd.length > 0) {
                operations.push({
                    op: 'ADD',
                    path: 'metadata',
                    value: this.metadatasToAdd.reduce((previous, current) => ({
                        ...previous,
                        [current.key]: current.value
                    }), {})
                });
            }

            if (this.metadatasToRemove.length > 0) {
                operations.push({
                    op: 'REMOVE',
                    path: 'metadata',
                    value: this.metadatasToRemove
                });
            }
        }

        const updatableOrRemovableField = (action: 'unchanged' | 'modify' | 'remove', path: string, field?: string) => {
            if (action !== BulkAction.UNCHANGED) {
                if (action === BulkAction.MODIFY && field) {
                    operations.push({
                        op: 'REPLACE',
                        path: path,
                        value: field
                    });
                } else if (action === BulkAction.REMOVE) {
                    operations.push({
                        op: 'REMOVE',
                        path: path
                    });
                }
            }
        };

        updatableOrRemovableField(buildProjectManagerAction, 'buildProjectManager', this.bulkEditForm.value.buildProjectManager);
        updatableOrRemovableField(deliveryManagerAction, 'deliveryManager', this.bulkEditForm.value.deliveryManager);
        updatableOrRemovableField(backupDeliveryManagerAction, 'backupDeliveryManager', this.bulkEditForm.value.backupDeliveryManager);
        updatableOrRemovableField(businessProjectAction, 'businessProject', this.bulkEditForm.value.businessProject);

        if (appManagerAction === BulkAction.ADD && this.bulkEditForm.value.addAppManager) {
            operations.push({
                op: 'ADD',
                path: 'appManagers',
                value: [this.bulkEditForm.value.addAppManager]
            });
        }

        if (appManagerAction === BulkAction.REMOVE && this.bulkEditForm.value.removeAppManager) {
            operations.push({
                op: 'REMOVE',
                path: 'appManagers',
                value: [this.bulkEditForm.value.removeAppManager]
            });
        }

        return operations;
    }
}
